import Cookie from 'js-cookie';
import toLower from 'lodash/toLower';
import { Dispatch } from 'react';
import { useStripe, useElements, CardNumberElement, CardCvcElement } from '@stripe/react-stripe-js';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';

import { IAddress } from 'checkout/types/Address';
import { IBillingInfo } from 'checkout/types/Billing';
import { IPaymentMethod } from 'checkout/types/PaymentMethod';
import { IKlarnaSessionRequestBody, IPayPalSessionRequestBody } from 'checkout/types/Payment';
import { PaymentType } from 'shared/enums/PaymentType';
import { STORAGE } from 'shared/enums/SitePreferences';
import { actions } from 'checkout/state/checkoutActions';
import { isAllCredits, isUSA } from 'checkout/utils/orderUtils';
import { requestService } from 'shared/utils/services/requestService';
import {
  IAction,
  IAddressAction,
  IBillingInfoAction,
  ICreditCardSuccessAction,
  IErrorAction,
  IOrderSuccessAction,
  ITabChangeAction,
  IPaymentsAvailabilitySuccessAction,
} from 'checkout/types/ActionPayload';
import { IPartialOrder } from 'checkout/types/Order';
import { ITabName } from 'checkout/types/Tab';
import { useOrder } from 'checkout/hooks/useOrder';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { LOCALES } from 'localization/constants/configEnums';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'checkout.hooks.usePayments';

interface IUsePaymentsProps {
  dispatch: Dispatch<
    | IAction
    | IAddressAction
    | IBillingInfoAction
    | ICreditCardSuccessAction
    | IErrorAction
    | IOrderSuccessAction
    | ITabChangeAction
    | IPaymentsAvailabilitySuccessAction
  >;
  order?: IPartialOrder;
}

const usePayments = ({ dispatch, order }: IUsePaymentsProps) => {
  const elements = useElements();
  const stripe = useStripe();
  const router = useRouter();

  const { updateOrder, updateAlipayOrder, updateGiropayOrder } = useOrder({ dispatch, order });
  const { user } = useStoreSelector((state) => state.auth);

  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const orderNumber = order?.number;

  const orderLog = {
    stateOrderNumber: order?.number,
    orderId: order?.id,
    productId: order?.product?.id,
    productTemplateId: order?.product?.productTemplate?.id,
    slug: order?.product?.slug,
    shoeCondition: order?.product?.shoeCondition,
    size: order?.product?.size,
    paymentType: order?.paymentType,
    wantId: order?.wantId || null,
    isGuest: user?.isGuest,
    orderType: order?.wantId ? 2 : 1,
  };

  const listAvailablePaymentMethods = async ({ shippingRegion }: { shippingRegion: string }) => {
    dispatch({ type: actions.fetchAvailablePaymentMethodsRequest });
    const countryCode = Cookie.get(STORAGE.COUNTRY);
    try {
      const { paymentMethods }: { paymentMethods: IPaymentMethod[] } = await requestService({
        url: '/web-api/v1/payments_availability/list_available_payment_methods',
        method: 'post',
        data: JSON.stringify({
          country_code: countryCode,
          currency: Cookie.get(STORAGE.CURRENCY),
          final_price_cents: order?.localizedFinalPriceCents?.amount,
          goat_storage: false,
          // orderTypes: 1 is regular purchase, 2 is offer, 3 is drop
          order_type: order?.wantId ? 2 : 1,
          shipping_region: shippingRegion,
        }),
      });

      const availablePaymentMethods = {};
      for (const paymentMethod of paymentMethods) {
        availablePaymentMethods[toLower(paymentMethod.name)] = paymentMethod;
      }

      dispatch({
        type: actions.fetchAvailablePaymentMethodsSuccess,
        payload: availablePaymentMethods,
      });
    } catch (error) {
      dispatch({ type: actions.fetchAvailablePaymentMethodsFailure, payload: error.message });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout: listAvailablePaymentMethods()';
        event.addMetadata('checkout', { orderNumber, countryCode, shippingRegion, ...orderLog });
      });
    }
  };

  const connectBillingInfoToOrder = async (billingInfoId: number, manualOrderNumber?: number) => {
    const orderNumberToConnect = orderNumber || manualOrderNumber;

    if (!billingInfoId) {
      dispatch({
        type: actions.updateOrderFailure,
        payload: t(
          `${TP}.noBillingInfoId`,
          'Unable to select payment, please try a different payment or add a new one.',
        ),
      });

      const error = new Error(
        `No billing info id for selected billing info, order number: ${orderNumberToConnect}`,
      );

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: orderNumberToConnect });
      });

      return;
    }

    if (!orderNumberToConnect) {
      dispatch({
        type: actions.updateOrderFailure,
        payload: t(
          `${TP}.noOrderNumberToConnect`,
          'There was an issue storing your order, please return to the product page.',
        ),
      });

      const error = new Error(
        `No order number when selecting billing info, billing info id: ${billingInfoId}`,
      );

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: null });
      });

      return;
    }

    await updateOrder({ orderNumber: orderNumberToConnect, payload: { billingInfoId } });
  };

  const createBillingAddress = async (address: IAddress): Promise<IAddress | void> => {
    dispatch({ type: actions.createAddressRequest });

    try {
      const international = !isUSA(address.countryCode) ? '_international' : '';

      const billingAddress = await requestService({
        url: `/web-api/v1/addresses/billing${international}`,
        method: 'post',
        data: JSON.stringify({
          address: { ...address, addressType: 'billing' },
        }),
      });

      dispatch({ type: actions.createAddressSuccess, payload: billingAddress });

      return billingAddress;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        return;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      dispatch({
        type: actions.createAddressFailure,
        payload: errorMessage,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }
  };

  const createStripeBillingInfo = async ({
    billingAddressId,
    name,
    stripeToken,
    paymentType,
  }: {
    billingAddressId?: number;
    name: string;
    stripeToken: string;
    paymentType: PaymentType;
  }): Promise<IBillingInfo | null> => {
    dispatch({ type: actions.createBillingInfoRequest });

    try {
      const billingInfo = await requestService({
        url: `/web-api/v1/billing_infos/${paymentType}`,
        method: 'post',
        data: {
          billingInfo: {
            stripeToken,
            name,
            ...(billingAddressId && { billingAddressId }),
          },
        },
      });

      dispatch({ type: actions.createBillingInfoSuccess, payload: billingInfo });

      return billingInfo;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        return;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      dispatch({
        type: actions.createBillingInfoFailure,
        payload: errorMessage,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }
  };

  /**
   * The payment flows for Giropay and Alipay are through Stripe's old API for third party vendors
   * that are no longer fully documented on their site. The transaction is processed as an async background task
   * by the backend so we have no way of knowing when it processes or if it is sucessful.
   *
   * @todo: upgrade Stripe API for the backend so we can fully use the new payment flow that includes
   * transaction status and error handling.
   *
   * The user will receive an email from the backend when the transaction fails or succeeds.
   */
  const createStripeSource = async (paymentType: PaymentType, shippingAddress: IAddress) => {
    try {
      if (!stripe) {
        const error = new Error('Stripe unavailable.');

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        throw error;
      }

      if (!order?.id) {
        const error = new Error('No order present. Cannot create stripe source.');

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        throw error;
      }

      const currency = paymentType === PaymentType.Giropay ? 'EUR' : 'USD';
      const finalPriceCents =
        paymentType === PaymentType.Giropay
          ? order?.localizedFinalPriceCents?.amount
          : order?.finalPriceCents;

      const redirectURL = `checkout/${orderNumber}/success`;

      const sourceParams = {
        type: paymentType,
        amount: finalPriceCents,
        currency,
        redirect: {
          return_url:
            router?.locale === LOCALES.enUS
              ? `${window.location.origin}/${redirectURL}`
              : `${window.location.origin}/${router?.locale}/${redirectURL}`,
        },
        // only necessary for Giropay
        owner: {
          name: shippingAddress?.name,
        },
      };

      const response = await stripe.createSource(sourceParams);

      const billingInfo = await createStripeBillingInfo({
        stripeToken: response?.source?.id,
        name: shippingAddress?.name,
        paymentType,
      });

      if (billingInfo) {
        await updateOrder({ orderNumber, payload: { billingInfoId: billingInfo.id } });
      }

      /**
       * NOTE: If user selected Alipay or Giropay and went back to the payment screen without
       * completing their order, they should be allowed to retry again.
       */
      const retry = order?.status === 'awaiting_payment';

      if (paymentType === PaymentType.Alipay) {
        await updateAlipayOrder(order?.number, retry);
      } else if (paymentType === PaymentType.Giropay) {
        await updateGiropayOrder(order?.number, retry);
      }

      window.location.href = response?.source?.redirect?.url;
    } catch (error) {
      dispatch({ type: actions.createBillingInfoFailure, payload: error.message });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });

      throw error;
    }
  };

  const createBillingInfo = async (
    billingInfo,
    paymentType: PaymentType,
  ): Promise<IBillingInfo | null> => {
    dispatch({ type: actions.createBillingInfoRequest });

    try {
      const resp = await requestService({
        url: `/web-api/v1/billing_infos/${paymentType}`,
        method: 'post',
        data: {
          billingInfo,
        },
      });

      dispatch({ type: actions.createBillingInfoSuccess, payload: resp });

      return resp;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        return null;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      dispatch({
        type: actions.createBillingInfoFailure,
        payload: errorMessage,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });
    }
  };

  const createKlarnaSession = async (
    klarnaSessionOptions: IKlarnaSessionRequestBody,
  ): Promise<boolean> => {
    dispatch({ type: actions.createKlarnaSessionRequest });

    try {
      const redirectURL = await requestService({
        url: '/web-api/v1/checkout/klarna',
        method: 'post',
        data: klarnaSessionOptions,
      });

      dispatch({ type: actions.createKlarnaSessionSuccess, payload: redirectURL });

      void router?.push(redirectURL);

      return true;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        return false;
      }

      const errorMessage =
        error?.response?.data?.localized_messages?.[0] ||
        error?.response?.data?.messages?.[0] ||
        '';

      dispatch({
        type: actions.createKlarnaSessionFailure,
        payload: errorMessage,
      });
      return false;
    }
  };

  const createPayPalSession = async (
    payPalSessionOptions: IPayPalSessionRequestBody,
  ): Promise<boolean> => {
    dispatch({ type: actions.createPayPalSessionRequest });

    try {
      const redirectURL: string = await requestService({
        url: '/web-api/v1/checkout/paypal',
        method: 'post',
        data: { isGuestCheckoutEnabled, ...payPalSessionOptions },
      });

      dispatch({ type: actions.createPayPalSessionSuccess, payload: redirectURL });

      // Redirects user to PayPal's checkout page to complete the purchase.
      void router?.push(redirectURL);

      return true;
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch({
          type: actions.sessionTimeoutFailure,
          payload: t(`${TP}.sessionExpired`, 'Your session expired.'),
        });

        return false;
      }

      const errorMessage =
        error.response?.data?.localized_messages?.[0] ||
        error.response?.data?.localized_messages ||
        error.response?.data?.messages?.[0] ||
        error.response?.data?.messages ||
        '';

      Bugsnag.notify(errorMessage, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', {
          _message: 'Error creating PayPal v2 session.',
          orderNumber,
          ...orderLog,
        });
      });

      dispatch({
        type: actions.createPayPalSessionFailure,
        payload: errorMessage,
      });
      return false;
    }
  };

  const addNewCreditCard = async (address: IAddress, guestOrderNumber?: number) => {
    const cardNumberElement = elements?.getElement(CardNumberElement);

    for (const [key, value] of Object.entries(address)) {
      if (!value) {
        delete address[key];
      }
    }

    try {
      if (!order?.number && !guestOrderNumber) {
        const error = new Error("No order number, can't create stripe payment");

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: null });
        });

        throw error;
      }

      if (!stripe || !cardNumberElement) {
        const error = new Error('Can not add new card. Stripe is unavailable');

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber });
        });

        throw error;
      }

      dispatch({ type: actions.createBillingInfoRequest });

      const { token, error } = await stripe.createToken(cardNumberElement, {
        name: address.name,
        address_line1: address.address1,
        address_line2: address.address2 || '',
        address_city: address.city,
        address_state: address.state,
        address_zip: address.postalCode,
        address_country: address.countryCode,
      });

      const stripeToken = token?.id;

      if (error) {
        throw error;
      }

      if (!stripeToken) {
        const _error = new Error(
          `No stripe token, can't create stripe payment for ${
            (orderNumber || String(guestOrderNumber)) as string
          }`,
        );

        Bugsnag.notify(_error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', {
            orderNumber: orderNumber || guestOrderNumber,
            guest: user.isGuest,
          });
        });

        throw _error;
      }

      // CHECKOUT TODO: add billing address form validation from server errors
      // potentially combine useShippingAddresses to useAddresses
      const billingAddress = await createBillingAddress(address);

      if (!billingAddress || !billingAddress.id) {
        const _error = new Error('Error creating billing address');

        Bugsnag.notify(_error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', {
            orderNumber: orderNumber || guestOrderNumber,
            guest: user.isGuest,
          });
        });

        throw _error;
      }

      const billingInfo = await createStripeBillingInfo({
        billingAddressId: billingAddress?.id,
        name: address.name,
        stripeToken,
        paymentType: PaymentType.CreditCard,
      });

      if (billingInfo) {
        const url = isGuestCheckoutEnabled
          ? {
              asPath: `/checkout?orderNumber=${router.query.orderNumber as string}`,
              query: { orderNumber: router.query.orderNumber },
            }
          : {
              pathname: '/checkout/[orderNumber]',
              query: { orderNumber: router.query.orderNumber },
            };

        // NOTE: remove any query params from appended by other payment providers
        void router?.replace(url, null, {
          shallow: true,
        });

        await connectBillingInfoToOrder(billingInfo.id, guestOrderNumber);

        dispatch({ type: actions.changeTab, payload: ITabName.REVIEW });
      }
    } catch (error) {
      dispatch({ type: actions.createBillingInfoFailure, payload: error.message });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', {
          orderNumber: orderNumber || guestOrderNumber,
          guest: user.isGuest,
        });
      });

      throw error;
    }
  };

  const verifyCreditCard = async () => {
    const cvcElement = elements?.getElement(CardCvcElement);
    try {
      if (!order.id) {
        throw new Error('No order available. Can not verify credit card.');
      }

      if (!stripe || !cvcElement) {
        throw new Error('Unable to verify your credit card. Stripe is unavailable');
      }

      dispatch({ type: actions.verifyCreditCardRequest });

      const { token, error: stripeError } = await stripe.createToken('cvc_update', cvcElement);
      const stripeToken = token?.id;

      if (stripeError) {
        // NOTE: cannot pass in stripe error directly to bugsnag
        throw new Error(stripeError?.message);
      }

      if (!stripeToken) {
        throw new Error(`No stripe token, can't verify credit card for order ${orderNumber}`);
      }

      await requestService({
        url: `/web-api/v1/orders/${orderNumber}/verify_billing`,
        method: 'post',
        data: { cvcUpdateToken: stripeToken },
      });

      dispatch({ type: actions.verifyCreditCardSuccess });
    } catch (error) {
      dispatch({
        type: actions.verifyCreditCardFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'Checkout: verifyCreditCard';
        event.addMetadata('checkout', { orderNumber, ...orderLog });
      });
    }
  };

  const redirectToAfterpay = async () => {
    if (!orderNumber) {
      const error = new Error(
        'No order number present when user selects AfterPay, in select payment method',
      );

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });

      throw error;
    }

    if (isAllCredits(order)) {
      return false;
    }

    try {
      window.AfterPay.initialize({ countryCode: 'US' });

      const baseURL =
        router?.locale === LOCALES.enUS
          ? `${window.location.origin}/checkout`
          : `${window.location.origin}/${router?.locale}/checkout`;

      const redirectURL = [
        `${baseURL}`,
        isGuestCheckoutEnabled ? '?orderNumber=' : '/',
        orderNumber,
      ].join('');

      const { afterpayOrderToken } = await requestService({
        url: `/web-api/v1/orders/${orderNumber}/afterpay`,
        method: 'post',
        data: {
          finalPriceCents: order.localizedFinalPriceCents.amountUsdCents,
          redirectConfirmUrl: redirectURL,
          redirectCancelUrl: redirectURL,
        },
      });

      window.AfterPay.redirect({ token: afterpayOrderToken });
    } catch (error) {
      dispatch({ type: 'paymentMethodRequestFailure', payload: error?.message || error });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber });
      });

      return false;
    }

    return true;
  };

  return {
    addNewCreditCard,
    connectBillingInfoToOrder,
    createBillingAddress,
    createBillingInfo,
    createKlarnaSession,
    createPayPalSession,
    createStripeBillingInfo,
    createStripeSource,
    listAvailablePaymentMethods,
    redirectToAfterpay,
    verifyCreditCard,
  };
};

export { usePayments };
